import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';

import { HeaderLink, HeaderDrawer, LocaleSelector } from './index.js';
import { HeaderLogo, IconChevronSprite } from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';
import MobileNavigation from '$partials/global/mobile-navigation.jsx';
import { useGlobalLayout } from '$contexts/global-layout';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const Header = ({
    isHomePage = false,
    menu,
    stylingWhite,
    setStylingWhite = () => { }
}) => {
    // hooks
    const {
        formatURI,
        translate: t,
    } = useLocale();

    const { headerOpen, setIsHeaderVisibleOnScrollUp } = useGlobalLayout();

    // Scroll behavior state
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const [isAtTop, setIsAtTop] = useState(true);
    const [buttonClass, setButtonClass] = useState('button--primary');
    const [headerClasses, setHeaderClasses] = useState('');
    const [logoBgStyle, setLogoBgStyle] = useState('transparent');
    const [transitionsEnabled, setTransitionsEnabled] = useState(true);
    const lastScrollY = useRef(0);
    const headerRef = useRef(null);

    // Update styling based on whether we're at the top of the page
    useEffect(() => {
        // Don't update styling if header drawer is open
        if (headerOpen) {
            return;
        }

        if (isAtTop) {
            if (isHomePage) {
                setButtonClass('button--primary');
                setHeaderClasses('bg-transparent text-white');
                setLogoBgStyle('transparent');
                setStylingWhite(true);
            } else {
                setButtonClass('button--primary');
                setHeaderClasses('bg-white text-black');
                setLogoBgStyle('black');
                setStylingWhite(false);
            }
        } else {
            setButtonClass('button--header-up');
            setHeaderClasses('bg-sand text-black');
            setLogoBgStyle('black');
            setStylingWhite(false);
        }
    }, [isAtTop, isHomePage, headerOpen]);

    // Initial check for scroll position on mount
    useEffect(() => {
        const scrollThreshold = 10;
        const currentScrollY = window.scrollY || window.pageYOffset;
        setIsAtTop(currentScrollY < scrollThreshold);
    }, []);

    // Disable transitions when drawer opens/closes, re-enable after delay
    useEffect(() => {
        setTransitionsEnabled(false);
        const timer = setTimeout(() => {
            setTransitionsEnabled(true);
        }, 350); // Slightly longer than transition duration to ensure it completes

        return () => clearTimeout(timer);
    }, [headerOpen]);

    // Scroll event handler
    useEffect(() => {
        const handleScroll = () => {

            const currentScrollY = window.scrollY;
            const scrollThreshold = 10; // Minimum scroll distance to trigger hide/show

            // Update isAtTop state based on scroll position
            setIsAtTop(currentScrollY < scrollThreshold);

            // Always show header at the top of the page
            if (currentScrollY < scrollThreshold) {
                setIsHeaderVisible(true);
                if (setIsHeaderVisibleOnScrollUp) { setIsHeaderVisibleOnScrollUp(true); }
            } else {
                // Hide when scrolling down, show when scrolling up
                if (currentScrollY > lastScrollY.current && currentScrollY > scrollThreshold) {
                    setIsHeaderVisible(false);
                    if (setIsHeaderVisibleOnScrollUp) { setIsHeaderVisibleOnScrollUp(false); }
                } else if (currentScrollY < lastScrollY.current) {
                    setIsHeaderVisible(true);
                    if (setIsHeaderVisibleOnScrollUp) { setIsHeaderVisibleOnScrollUp(true); }
                }
            }

            lastScrollY.current = currentScrollY;
        };
        const headerRefNode = headerRef.current;
        if (headerOpen) {
            setHeaderClasses('bg-sand text-black translate-y-0 z-10 box-shadow-navigation z-50');
            setLogoBgStyle('white');
            disableBodyScroll(headerRefNode);
        }

        if (!headerOpen) {
            clearAllBodyScrollLocks();
            handleScroll();
        }
        // Throttle scroll events for better performance
        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [headerOpen]);

    // misc
    if (!menu) { return null; }

    // Format links helper function
    const createMenuItem = (item, title, index) => {
        const collection = item?.dropdown?.length > 0 ? item?.dropdown : item?.single;
        const dropdown = collection?.length > 0;

        return {
            collection,
            dropdown,
            index,
            title
        };
    };

    const menuItems = [
        createMenuItem({ dropdown: menu?.link1DropdownCollection?.items, single: menu?.link1 }, menu?.link1Title, 1),
        createMenuItem({ dropdown: menu?.link2DropdownCollection?.items, single: menu?.link2 }, menu?.link2Title, 2),
        createMenuItem({ dropdown: menu?.link3DropdownCollection?.items, single: menu?.link3 }, menu?.link3Title, 3),
        createMenuItem({ dropdown: menu?.link4DropdownCollection?.items, single: menu?.link4 }, menu?.link4Title, 4),
    ];

    return (
        <>
            <div
                className={`${headerClasses} fixed w-full z-50 top-0 ${transitionsEnabled ? 'transition-all duration-300 ease-in-out' : ''}`}
                ref={headerRef}
                style={{ transform: isHeaderVisible ? 'translateY(0)' : 'translateY(-100%)' }}>
                <div className='layout-container h-[40px] md:h-[60px] flex items-center'>
                    <header className='w-full relative px-10 sm:px-5 md:px-10 flex items-center justify-between type-style-7 font-semibold'>
                        <a
                            className='block'
                            href={formatURI('/')}>
                            <HeaderLogo bgStyle={logoBgStyle} />
                        </a>

                        <div className='md:hidden flex-grow text-right flex items-center justify-end'>
                            <MobileNavigation menu={menuItems} />
                        </div>

                        <nav className='navigation hidden md:block w-full text-center self-center'>
                            <ul className='navigation__margin flex justify-around items-center'>
                                {menuItems?.map((item, idx) => {
                                    return (
                                        (item?.collection || item?.dropdown || item?.title) ? (
                                            <HeaderLink
                                                index={item.index}
                                                key={idx}
                                                linkItem={item} />
                                        ) : (
                                            null
                                        )
                                    );
                                })}
                            </ul>
                        </nav>

                        <div className='hidden md:block'>
                            <a
                                className={`block button h-[36px] ${buttonClass}`}
                                href={`${formatURI('/contact')}`}>
                                <span>{t('Get In Touch')}</span>
                                <span className='button__icon button__icon--animate'>
                                    <IconChevronSprite />
                                </span>
                            </a>
                        </div>
                    </header>

                    <LocaleSelector stylingWhite={stylingWhite} />
                </div>

                {menuItems?.map((link, i) => {
                    if (link?.dropdown) {
                        return (
                            <HeaderDrawer
                                headerOpen={headerOpen === i + 1}
                                key={i}
                                links={link?.collection} />
                        );
                    }
                    return null;
                })?.filter(Boolean)}
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    isHomePage: PropTypes.bool,
    menu: PropTypes.shape({
        link1: PropTypes.object,
        link1DropdownCollection: PropTypes.object,
        link1Title: PropTypes.string,
        link2: PropTypes.object,
        link2DropdownCollection: PropTypes.object,
        link2Title: PropTypes.string,
        link3: PropTypes.object,
        link3DropdownCollection: PropTypes.object,
        link3Title: PropTypes.string,
        link4: PropTypes.object,
        link4DropdownCollection: PropTypes.object,
        link4Title: PropTypes.string,
    }),
    stylingWhite: PropTypes.bool,
    setStylingWhite: PropTypes.func
};

Header.propTypes = propTypes;

export default Header;
